/**
 * Watch-page keyboard shortcuts — single source of truth.
 *
 * This data drives BOTH the help dialog (shortcuts-dialog.tsx) and the docs.
 * The actual key handling lives where the action lives:
 *   - player-local keys (Space/K, M, F, arrow frame-step) → video-player.tsx
 *   - page-level keys (C, ?) + composer focus/Esc            → watch.tsx + comment-composer.tsx
 * Keep the labels here in sync with those handlers.
 */
export type ShortcutItem = { keys: string[]; label: string };
export type ShortcutGroup = { title: string; items: ShortcutItem[] };

export const WATCH_SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "Playback",
    items: [
      { keys: ["Space"], label: "Play / pause" },
      { keys: ["K"], label: "Play / pause" },
      { keys: ["M"], label: "Mute / unmute" },
      { keys: ["F"], label: "Fullscreen" },
    ],
  },
  {
    title: "Frames",
    items: [
      { keys: ["←"], label: "Step back 1 frame" },
      { keys: ["→"], label: "Step forward 1 frame" },
      { keys: ["Shift", "←"], label: "Step back 10 frames" },
      { keys: ["Shift", "→"], label: "Step forward 10 frames" },
    ],
  },
  {
    title: "Comments",
    items: [
      { keys: ["C"], label: "Comment on this frame (pauses + focuses the box)" },
      { keys: ["Enter"], label: "Send comment" },
      { keys: ["Shift", "Enter"], label: "New line in a comment" },
      { keys: ["Esc"], label: "Leave the comment box" },
    ],
  },
  {
    title: "General",
    items: [{ keys: ["Shift", "?"], label: "Show this shortcuts panel" }],
  },
];

/** True when focus is in a text-entry element — used to suppress single-key
 *  shortcuts so typing a comment never triggers the player. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}
